import { useState } from "react";
import type { ConnectionState, DeviceState } from "../types";
import { config } from "../config";
import { deriveBanner } from "../lib/banner";
import { useNow } from "../hooks/useNow";
import {
  useDeviceHistory,
  type HistoryWindow,
  type AvailabilityRange,
} from "../hooks/useHistory";
import { Banner } from "./Banner";
import { Tiles } from "./Tiles";
import { AvailabilityTimeline } from "./AvailabilityTimeline";

interface Props {
  connection: ConnectionState;
  device: DeviceState;
}

export function DeviceCard({
  connection,
  device,
}: Props) {
  const banner = deriveBanner(connection, device);
  const window: HistoryWindow = "1h";
  const [range, setRange] = useState<AvailabilityRange>("1h");

  // Live up/down derived from the SSE state, re-evaluated every second so a
  // device that goes silent flips to "down" on its own (same staleness rule as
  // the dashboard summary). "unknown" until the first heartbeat arrives.
  const now = useNow();
  const liveState: "up" | "down" | "unknown" =
    device.lastTelemTs == null
      ? "unknown"
      : device.status !== "offline" &&
          now - device.lastTelemTs <= config.offlineThresholdMs
        ? "up"
        : "down";

  const history = useDeviceHistory(device.meta.id, window, range, liveState);

  // Real measured downtime, or 0 when there's no history yet — never a fake value.
  const downtimeMs = history.availability?.downtimeMs ?? 0;

  return (
    <section className="device">
      <header className="device-header">
        <h2 className="device-title">{device.meta.name}</h2>
        <span className="device-id">
          <code>{device.meta.id}</code>
        </span>
      </header>
      <Banner banner={banner} />
      <Tiles telemetry={device.telemetry} downtimeMs={downtimeMs} />
      <AvailabilityTimeline
        data={history.availability}
        range={range}
        onRangeChange={setRange}
        live={{ state: liveState, t: now }}
      />
    </section>
  );
}
